<script setup>
import { ref, computed, onMounted } from 'vue';
import { Field, useForm } from 'vee-validate';
import { useToast } from 'primevue/usetoast';
import * as yup from 'yup';
import CertidaoService from '@/services/CertidaoService';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Select from 'primevue/select';

const props = defineProps({ menuCertidoesVisivel: Boolean, emitindo: Boolean });

const emit = defineEmits(['loading', 'certidoes']);

const consultando = ref(false);
const configuracoesEmissao = ref({});
const exigeCpfCnpjOpcoes = ref({
    matricula: false,
    inscricao: false
});
const exibeCpfCnpj = ref(false);

const emitirPorOptions = ref([
    { label: 'Matrícula', value: 'M' },
    { label: 'Inscrição', value: 'I' },
    { label: 'Nome/Razão Social', value: 'C' }
]);

const schemaConsulta = computed(() =>
    yup.object({
        emitir_por: yup.string().required('Selecione o tipo de identificação.'),
        identificador: yup.string().when('emitir_por', {
            is: (val) => val && val !== 'C',
            then: (schema) => schema.required(`Preencha a ${identificadorLabel.value}.`),
            otherwise: (schema) => schema.notRequired()
        }),
        cpf_cnpj: exibeCpfCnpj.value ?
                  yup.string().nullable().cpfCnpj().required('Preencha o CPF ou CNPJ.') :
                  yup.string().nullable().cpfCnpj()
    })
);

const {
    handleSubmit,
    values: valoresConsulta,
    resetForm,
    setFieldValue
} = useForm({
    validationSchema: schemaConsulta,
    initialValues: {
        emitir_por: '',
        identificador: '',
        cpf_cnpj: ''
    }
});

const identificadorLabel = computed(() => {
    const labels = {
        M: 'Matrícula',
        I: 'Inscrição',
        C: 'CGM'
    };

    return labels[valoresConsulta.emitir_por] ?? 'Identificador';
});

const identificadorPlaceholder = computed(() => {
    if (!valoresConsulta.emitir_por) return 'Selecione como deseja emitir';

    return valoresConsulta.emitir_por === 'C'
        ? 'Opcional para emissão por CGM'
        : 'Digite a matrícula ou inscrição';
});

const exibirIdentificador = computed(() => {
    return valoresConsulta.emitir_por && valoresConsulta.emitir_por !== 'C';
});

const identificadorHabilitado = computed(() => {
    return Boolean(valoresConsulta.emitir_por && valoresConsulta.emitir_por !== 'C');
});

const toast = useToast();

onMounted(async () => {
    await carregarExigeCpfCnpj();
    await carregarConfiguracoes();
});

const carregarConfiguracoes = async () => {
    emit('loading', true);

    try {
        const resp = await CertidaoService.obterConfig();

        configuracoesEmissao.value = {
            certidoes_inscricao: resp.data.certidoes_inscricao ?? true,
            certidoes_matricula: resp.data.certidoes_matricula ?? true,
            certidoes_nome_razao_social: resp.data.certidoes_nome_razao_social ?? true,
            configurado: resp.data.configurado ?? false
        };
    } catch (e) {
        toast.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Não foi possível buscar as configurações.',
            life: 3000
        });
    } finally {
        emit('loading', false);
    }
}

const carregarExigeCpfCnpj = async () => {
    emit('loading', true);

    try {
        const resp = await CertidaoService.exigeCpfCnpj({});

        exigeCpfCnpjOpcoes.value = resp.data.data ?? resp.data;
    } catch (e) {
        console.error(e);
    } finally {
        emit('loading', false);

        onChangeCpfCnpj();
    }
}

const submitConsulta = handleSubmit(async (values) => {
    consultando.value = true;

    emit('certidoes', []);

    const payload = {
        emitir_por: values.emitir_por
    };

    if (values.emitir_por !== 'C') payload.identificador = onlyDigits(values.identificador);

    const cpfCnpj = onlyDigits(values.cpf_cnpj);

    if (cpfCnpj) payload.cpf_cnpj = cpfCnpj;

    try {
        const resp = await CertidaoService.buscar(payload);

        const rawData = resp.data.data ?? resp.data;
        const items = Array.isArray(rawData) ? rawData : [rawData];
        const certidoes = items
            .filter((item) => item?.tipo && item?.titulo)
            .map((item) => ({
                tipo: item.tipo,
                titulo: item.titulo,
                identificador: item.identificador
            }));

        emit('certidoes', certidoes);

        if (certidoes.length === 0) {
            toast.add({
                severity: 'info',
                summary: 'Atenção',
                detail: 'Nenhuma certidão disponível para os dados informados.',
                life: 3000
            });
        }
    } catch (e) {
        handleApiError(e, 'Não foi possível consultar as certidões fiscais.');
    } finally {
        consultando.value = false;
    }
});

const onChangeCpfCnpj = () => {
    if (valoresConsulta.emitir_por === 'M') {
        exibeCpfCnpj.value = exigeCpfCnpjOpcoes.value.matricula;
    } else if (valoresConsulta.emitir_por === 'I') {
        exibeCpfCnpj.value = exigeCpfCnpjOpcoes.value.inscricao;
    } else if (valoresConsulta.emitir_por === 'C') {
        exibeCpfCnpj.value = true;
    }

    if (!exibeCpfCnpj.value) setFieldValue('cpf_cnpj', null);
}

const onlyDigits = (value) => {
    return (value || '').replace(/\D/g, '');
}

const handleApiError = (e, fallbackMsg) => {
    const validationErrors = e.response?.data?.errors;
    const errorMessage = e.response?.data?.message;

    if (validationErrors) {
        const messages = Object.values(validationErrors).flat().join('<br/>');

        toast.add({ severity: 'info', summary: 'Atenção', detail: messages, life: 5000 });

        return;
    }

    if (errorMessage) {
        toast.add({ severity: 'info', summary: 'Atenção', detail: errorMessage, life: 5000 });

        return;
    }

    toast.add({ severity: 'warn', summary: 'Atenção', detail: fallbackMsg, life: 5000 });
}

const limparForm = () => {
    resetForm();

    exibeCpfCnpj.value = false;

    emit('certidoes', []);
}

defineExpose({ valoresConsulta });
</script>

<template>
    <Card v-if="!props.menuCertidoesVisivel" class="consulta-card">
        <template #title>
            <div class="card-header">
                <h2>Consultar disponibilidade</h2>
            </div>
        </template>

        <template #content>
            <form @submit="submitConsulta" class="form-grid gap-2">
                <div class="field">
                    <label for="emitir_por">Emitir por</label>

                    <Field name="emitir_por" v-slot="{ value, handleChange, errorMessage }">
                        <Select
                            :model-value="value"
                            :options="emitirPorOptions"
                            option-label="label"
                            option-value="value"
                            placeholder="Selecione"
                            @change="(e) => {
                                handleChange(e.value);
                                onChangeCpfCnpj();
                            }"
                        />

                        <small v-if="errorMessage" class="text-danger">
                            {{ errorMessage }}
                        </small>
                    </Field>
                </div>

                <div v-if="exibirIdentificador" class="field">
                    <label for="identificador">{{ identificadorLabel }}</label>

                    <Field name="identificador" v-slot="{ field, errorMessage }">
                        <InputText
                            v-bind="field"
                            id="identificador"
                            :placeholder="identificadorPlaceholder"
                            inputmode="numeric"
                            maxlength="30"
                            :disabled="!identificadorHabilitado"
                            class="w-full"
                        />

                        <small v-if="errorMessage" class="text-danger">{{ errorMessage }}</small>
                    </Field>
                </div>

                <div v-if="exibeCpfCnpj" class="field full-width">
                    <label for="cpf_cnpj">CPF ou CNPJ</label>

                    <Field name="cpf_cnpj" v-slot="{ field, errorMessage }">
                        <InputText
                            v-bind="field"
                            id="cpf_cnpj"
                            placeholder="Informe sem máscara para envio"
                            inputmode="numeric"
                            maxlength="18"
                            class="w-full"
                        />

                        <small v-if="errorMessage" class="text-danger">{{ errorMessage }}</small>
                    </Field>
                </div>

                <div class="actions full-width">
                    <Button
                        label="Listar"
                        icon="pi pi-search"
                        severity="primary"
                        type="submit"
                        :loading="consultando"
                        :disabled="consultando || props.emitindo"
                    />

                    <Button
                        label="Limpar"
                        icon="pi pi-eraser"
                        severity="secondary"
                        outlined
                        type="button"
                        :disabled="consultando || props.emitindo"
                        @click="limparForm()"
                    />
                </div>
            </form>
        </template>
    </Card>
</template>

<style scoped>
.card-header h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-primary);
    margin: 0;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
}

.full-width {
    grid-column: 1 / -1;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field label {
    font-weight: 500;
    font-size: 0.875rem;
    color: #334155;
}

.actions {
    display: flex;
    gap: 12px;
}

@media (max-width: 767px) {
    .form-grid {
        grid-template-columns: 1fr;
    }

    .actions {
        flex-direction: column;
    }
}
</style>
